<template>
    <div>
        <section v-if="form.productLine">
            <div class="container">

                <q-card class="q-mb-lg">
                    <q-card-section :class="{'q-px-xl' : screenSize.gt.sm, 'q-px-md' : screenSize.lt.md}">
                        <img :src="form.productLine.logo" class="img-tutorial q-mt-md q-mb-sm" :title="form.productLine.name" style="height: 55px;" />
                        <p>Choose your product for the Activation options.</p>
                        <div class="row">
                            <div class="col-xs-12 col-sm-6 col-md-4">
                                <q-select outlined dense class="q-mb-md"
                                          bg-color="white"
                                          label="Product Type" stack-label
                                          autocomplete="off"
                                          @input="$router.push(`/activation/${form.productLine.name.toLowerCase()}`)"
                                          v-model="form.productLine" :options="productLines"
                                          option-label="name"
                                          hide-bottom-space />
                            </div>
                        </div>
                    </q-card-section>
                </q-card>

                <div style="float: right;">
                    <q-btn flat color="primary" no-caps class="row items-end col-xs-12 col-sm-auto" :to="`/product/${form.productLine.name.toLowerCase()}/licenseinfo`">
                        <q-icon name="mdi-information-outline" size="1.5em" class="q-mr-xs" /><span>License & Subscription Information</span>
                    </q-btn>
                </div>
                <div class="text-title">Product Activation</div>
                <p>Activation is required in order to continue using the software after the trial period has expired. If you have not yet purchased a license, you can do so here. If you have already purchased a license and need to activate or reactivate, choose the appropriate option below.</p>

                <q-card :flat="screenSize.xs" :class="{'bg-transparent' : screenSize.xs}">
                    <q-card-section :class="{'q-px-none q-pb-none' : screenSize.xs, 'q-pa-lg' : !screenSize.xs}">
                        <q-stepper v-model="step"
                                   ref="stepper"
                                   header-nav
                                   color="primary"
                                   :contracted="screenSize.xs"
                                   :class="{'bg-transparent' : screenSize.xs}"
                                   flat animated>
                            <q-step :name="1"
                                    title="Options"
                                    active-icon="mdi-format-list-bulleted"
                                    icon="mdi-format-list-bulleted"
                                    :done="step > 1">
                                <div v-if="form.productLine.options.reactivate">
                                    <div class="text-weight-medium q-mt-md"
                                         :class="{'text-subtitle1' : !screenSize.xs, 'text-body1' : screenSize.xs}">
                                        I want to activate my existing {{ form.productLine.name == 'PlumbingCAD' ? 'subscription' : 'license' }}...
                                    </div>
                                    <div class="q-pa-sm">
                                        <q-option-group :options="form.productLine.options.reactivate"
                                                        type="radio"
                                                        v-model="form.selectedOption" />
                                    </div>
                                </div>
                                <div v-if="form.productLine.options.renew">
                                    <div class="text-weight-medium q-mt-md"
                                         :class="{'text-subtitle1' : !screenSize.xs, 'text-body1' : screenSize.xs}">
                                        I want to renew my subscription...
                                    </div>
                                    <div class="q-pa-sm">
                                        <q-option-group :options="form.productLine.options.renew"
                                                        type="radio"
                                                        v-model="form.selectedOption" />
                                    </div>
                                </div>
                                <div v-if="form.productLine.options.upgrade">
                                    <div class="text-weight-medium q-mt-md"
                                         :class="{'text-subtitle1' : !screenSize.xs, 'text-body1' : screenSize.xs}">
                                        I want to upgrade my license...
                                    </div>
                                    <div class="q-pa-sm">
                                        <q-option-group :options="form.productLine.options.upgrade"
                                                        type="radio"
                                                        v-model="form.selectedOption" />
                                    </div>
                                </div>
                                <div v-if="form.productLine.options.new">
                                    <div class="text-weight-medium q-mt-md"
                                         :class="{'text-subtitle1' : !screenSize.xs, 'text-body1' : screenSize.xs}">
                                        I want to purchase and activate a new {{ form.productLine.name == 'PlumbingCAD' ? 'subscription' : 'license' }}...
                                    </div>

                                    <div class="q-pa-sm">
                                        <q-option-group :options="form.productLine.options.new"
                                                        type="radio"
                                                        v-model="form.selectedOption" />
                                    </div>
                                </div>
                            </q-step>

                            <q-step v-if="form.selectedOption !== 'newLicense' && form.selectedOption !== 'prepaid'"
                                    :name="2"
                                    title="Sign In"
                                    :icon="isLoggedIn ? 'mdi-check' : 'mdi-account'"
                                    :active-icon="isLoggedIn ? 'mdi-check' : 'mdi-account'"
                                    :disable="isLoggedIn"
                                    :done="step > 2 || isLoggedIn">
                                <div class="text-center q-my-xl">
                                    <p>Please sign in or create an account to continue.</p>
                                    <div class="row q-col-gutter-sm justify-center">
                                        <div class="col-xs-12 col-sm-auto">
                                            <q-btn label="Sign In" color="primary" @click="showLogin = true" class="full-width" />
                                        </div>
                                        <div class="col-xs-12 col-sm-auto">
                                            <q-btn outline label="Create New Account" color="primary" @click="$router.push('/newaccount')" class="full-width" />
                                        </div>
                                    </div>
                                </div>
                            </q-step>

                            <q-step :name="3"
                                    :title="stepData.caption"
                                    :icon="stepData.icon"
                                    :active-icon="stepData.icon"
                                    :disable="!isLoggedIn && form.selectedOption !== 'newLicense' && form.selectedOption !== 'prepaid'"
                                    style="min-height: 150px">
                                <div v-if="form.selectedOption == 'newLicense' && fullLicenseProducts.length > 0">
                                    <div class="q-mb-lg">
                                        <div class="text-subtitle1">Product Details</div>
                                        <p>Select the product edition you want to purchase.</p>
                                        <currency-select class="q-mb-lg" />
                                        <div class="row justify-start text-center q-col-gutter-lg">

                                            <div v-for="prod in fullLicenseProducts" :key="prod.id"
                                                 class="col-xs-12 col-sm-6 col-md-4 cursor-pointer"
                                                 @click="form.product = prod">
                                                <product :id="prod.id" :product="prod"
                                                         :selected="form.product && form.product.id == prod.id"></product>
                                            </div>
                                        </div>
                                        <q-inner-loading :showing="getProductsForPurchasePending">
                                            <div>
                                                <q-spinner color="primary"
                                                           :thickness="2"
                                                           size="3em" />
                                            </div>
                                        </q-inner-loading>
                                    </div>
                                    <div class="q-mb-lg">
                                        <validate-discount-code v-if="form.product"
                                                                @updateDiscountCode="updateDiscountCode"
                                                                :formDiscountCode="form.discountCode"
                                                                :product="form.product" />
                                    </div>
                                </div>
                                <div v-if="form.selectedOption == 'prepaid' && fullLicenseProducts.length > 0">
                                    <div class="q-mb-lg">
                                        <validate-code ref="validateCodeRef"
                                                       @updateDiscountCode="updateDiscountCode"
                                                       @updateProduct="updateProduct"
                                                       :formDiscountCode="form.discountCode"
                                                       :licenses="allProductLineProducts"
                                                       :product="form.product" />
                                    </div>

                                    <div v-if="this.form.product && this.form.discountCode" class="q-mb-lg">
                                        <div class="text-subtitle1">Product Details</div>

                                        <div class="row justify-start text-center q-col-gutter-lg">

                                            <div class="col-xs-12 col-sm-6 col-md-4">
                                                <product :id="form.product.id" :product="form.product"
                                                         :showPrice="false"
                                                         :selected="true"></product>
                                            </div>
                                        </div>
                                        <q-inner-loading :showing="getProductsForPurchasePending || isDiscountCodeValidPending">
                                            <div>
                                                <q-spinner color="primary"
                                                           :thickness="2"
                                                           size="3em" />
                                            </div>
                                        </q-inner-loading>
                                    </div>
                                </div>
                                <div v-if="form.selectedOption == 'newLicense' || form.selectedOption == 'prepaid' && fullLicenseProducts.length > 0">
                                    <div v-if="form.product" class="q-mb-lg">
                                        <div class="text-subtitle1">Software Install ID</div>
                                        <p>If you started from within the software's Activation Wizard, the install ID will already be displayed for you here. If it is not shown, then you will need to enter it now. This must be the install ID from the computer that you want to be activated on. When entering the install ID, be sure to include the dashes (ie. XXXX-XXXX-XXXX).</p>
                                        <p><a href="https://www.avenir-online.com/AvenirWeb/pdfs/Finding%20Install%20ID%20-%202019.pdf" target="_blank">Click here for help on finding your software's Install ID.</a></p>
                                        <div class="row">
                                            <validate-install-id ref="validateInstallIdRef"
                                                                 :required="form.selectedOption == 'prepaid'"
                                                                 :formInstallId="form.installId"
                                                                 :productCategoryId="form.product.productCategoryId"
                                                                 @updateInstallId="updateInstallId" />
                                        </div>
                                    </div>
                                </div>
                                <div v-if="(form.selectedOption == 'renew' || form.selectedOption == 'upgradePro' || form.selectedOption == 'upgradeMJ8') && stepData.multiLicenses">
                                    <div class="q-mb-lg">
                                        <multi-license-selection ref="multiLicenseSelectRef"
                                                                 :targetEdition="form.selectedOption"
                                                                 :product="form.product"
                                                                 :licenses="stepData.multiLicenses"
                                                                 :pending="getLicensesCustomerPending"
                                                                 @expirySyncDateSettingUpdated="getCustomerLicenses"
                                                                 @submitted="processMultiLicenseSelection" />
                                    </div>
                                </div>
                                <div v-if="form.selectedOption == 'reactivate'">

                                    <account-licenses :productLine="form.productLine"
                                                      :activateOnly="true" />

                                </div>
                            </q-step>

                            <template v-slot:navigation>
                                <q-stepper-navigation>
                                    <div class="row q-col-gutter-sm">
                                        <div class="col-xs-12 col-sm-auto">
                                            <q-btn @click="onContinueStep();" color="primary" label="Continue" v-if="showContinue" class="full-width" />
                                        </div>
                                        <div class="col-xs-12 col-sm-auto">
                                            <q-btn v-if="step > 1" flat color="primary" @click="$refs.stepper.previous();" label="Back" class="full-width" />
                                        </div>
                                    </div>
                                </q-stepper-navigation>
                            </template>
                        </q-stepper>
                    </q-card-section>
                </q-card>
            </div>
        </section>

        <dialog-subscription-reinstatement :show.sync="showReinstatementCostDialog"
                                           :items="reinstatementLicenseItems"/>
        <q-dialog v-model="showLogin">
            <login @close="closeLoginDialog" style="width: 100%; max-width: 375px;" />
        </q-dialog>
    </div>
</template>
<script>
    import { mapState } from 'vuex'
    import { productLines } from '@/plugins/productLines'

    import multiLicenseSelection from '@/components/MultiLicenseSelection'
    import login from '@/components/Login'
    import product from '@/components/Product'
    import currencySelect from '@/components/CurrencySelect'
    import validateInstallId from '@/components/ValidateInstallationID'
    import validateCode from '@/components/ValidateCode'
    import validateDiscountCode from '@/components/ValidateDiscountCode'
    import dialogSubscriptionReinstatement from '@/components/dialogs/DialogSubscriptionReinstatement'
    import accountLicenses from '@/components/AccountLicenses'

    export default {
        name: 'activation',
        props: {
            productLine: String,
        },
        components: {
            accountLicenses,
            login,
            product,
            currencySelect,
            validateInstallId,
            validateCode,
            validateDiscountCode,
            multiLicenseSelection,
            dialogSubscriptionReinstatement,
        },
        data() {
            return {
                showLogin: false,
                step: 1,
                productLines: Object.keys(productLines)?.map(key => {
                    return productLines[key]
                })?.filter(pl => pl?.name.toLowerCase() !== 'lengthwise'),
                form: {
                    selectedOption: 'reactivate',
                    productLine: null,
                    installId: '',
                    discountCode: '',
                    product: null,
                    activationCode: null,
                }
            }
        },

        methods: {
            processMultiLicenseSelection(licenses) {
                try {
                    if (licenses && licenses.length > 0) {

                        const payload = {
                            product: this.form.product,
                            currency: this.cart.currency,
                            licenses: licenses,
                        }
                        this.$store.dispatch('syncRenewUpgradeItems', payload)
                    }
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: processMultiLiceseSelection")
                    throw err;
                }
            },
            addToCart(product) {
                try {
                    if (product) {
                        this.$store.dispatch('addCartItem', {
                            currency: this.cart.currency,
                            product: product,
                            discountCode: this.form.discountCode,
                            installationID: this.form.installId,
                        })
                    }
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: addToCart")
                    throw err;
                }
            },
            onContinueStep() {
                try {
                    switch (this.step) {
                        case 1:
                            switch (this.form.selectedOption) {
                                case 'newLicense':
                                    this.selectDefaultProduct()
                                    break
                                case 'prepaid':
                                    this.form.product = null
                                    break
                                case 'renew':
                                    this.form.product = this.subscriptionRenewalProducts?.find(p => p.productCategoryID == this.form.productLine?.name)
                                    break
                                case 'upgradeMJ8':
                                    this.form.product = this.upgradeProducts?.find(p => p.productCategoryID == this.form.productLine?.name && p.edition == 'Professional' && p.id.includes('MJ8'))
                                    break
                                case 'upgradePro':
                                    this.form.product = this.upgradeProducts?.find(p => p.productCategoryID == this.form.productLine?.name && p.edition == 'Standard' && p.id.includes('PF'))
                                    break
                                case 'reactivate':
                                    this.form.product = null
                                    break
                                default:
                                    this.form.product = this.fullLicenseProducts[this.fullLicenseProducts.length - 1]
                            }
                            this.$refs.stepper.next();
                            break;
                        case 3:
                            switch (this.form.selectedOption) {
                                case 'newLicense':
                                    this.addToCart(this.form.product)
                                    this.$router.push('/store/cart').catch(err => { })
                                    break
                                case 'prepaid':
                                    if (this.form.discountCode && this.form.installId) {
                                        //if discount code is valid and install id is valid, add product to the cart and send to the cart page.

                                        this.addToCart(this.form.product)
                                        this.$router.push('/store/cart').catch(err => { })

                                    }
                                    break
                                case 'renew':
                                case 'upgradeMJ8':
                                case 'upgradePro':
                                    this.submitMultiLicenseSelection()
                                    break
                                case 'reactivate':
                                    break
                                default:
                            }
                            break;
                        default:
                            this.$refs.stepper.next();
                    }
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: onContinueStep")
                    throw err;
                }
            },
            submitMultiLicenseSelection() {
                try {
                    let component = this.$refs.multiLicenseSelectRef
                    if (typeof component.onSubmit === 'function') {
                        component.onSubmit()
                    }
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: submitMultiLicenseSelection")
                    throw err;
                }
            },
            updateDiscountCode(discountCode) {
                this.form.discountCode = discountCode
            },
            updateProduct(prod) {
                this.form.product = prod
            },
            updateInstallId(installId) {
                this.form.installId = installId
            },
            updateProductLine() {
                try {
                    if (this.productLine) {
                        this.form.productLine = this.productLines?.find(pl => pl.name.toLowerCase() == this.productLine.toLowerCase())
                        this.selectDefaultProduct()
                    }
                    if (this.$route.query.option) {
                        this.form.selectedOption = this.$route.query.option
                    }
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: updateProductLine")
                    throw err;
                }
            },
            selectDefaultProduct() {
                try {
                    switch (this.form.productLine?.name) {
                        case 'LoopCAD':
                            this.form.product = this.fullLicenseProducts.find(p => p.edition == "Professional")
                            break
                        case 'HeatCAD':
                            this.form.product = this.fullLicenseProducts.find(p => p.edition == "Professional")
                            break
                        case 'PlumbingCAD':
                            this.form.product = this.fullLicenseProducts[0]
                            break
                        default:
                            this.form.product = this.fullLicenseProducts[0]
                    }
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: selectDefaultProduct")
                    throw err;
                }
            },
            getProductsForPurchase() {
                try {
                    this.$store.dispatch('getProductsForPurchase', '').then(() => {
                        this.selectDefaultProduct()
                    })
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: getProductsForPurchase")
                    throw err;
                }
            },
            getCustomerLicenses() {
                try {
                    if (this.isLoggedIn && this.user) {
                        this.$store.dispatch('getLicensesCustomer', this.user.id)
                    }
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: getCustomerLicenses")
                    throw err;
                }
            },
            updateRoute(option) {
                try {
                    console.log(option)
                    let queryParams = {}
                    if (option)
                        queryParams.option = option

                    this.$router.replace({
                        path: this.$route.path,
                        query: queryParams,
                    }).catch(err => { })
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: updateRoute")
                    throw err;
                }
            },
            closeLoginDialog(buttonClicked) {
                this.showLogin = false;
            },
            addCustomErrorContext(err, location) {
                if (typeof err !== 'object' || err === null) return;

                const contextKey = "\\src\\views\\Activation.Vue";

                if (!err._customContext) {
                    err._customContext = {};
                }

                if (!err._customContext[contextKey]) {
                    err._customContext[contextKey] = {
                        errorThrownCallStack: [],
                        props: {
                            productLine: this.productLine,
                        },
                        data: {
                            step: this.step,
                            form: this.form,
                        },
                        computed: {
                            cart: this.cart,
                            reinstatementLicenseItems: this.reinstatementLicenseItems,
                            stepData: this.stepData,
                            allProductLineProducts: this.allProductLineProducts,
                            fullLicenseProducts: this.fullLicenseProducts,
                            subscriptionRenewalProducts: this.subscriptionRenewalProducts,
                            upgradeProducts: this.upgradeProducts,
                            renewLicenses: this.renewLicenses,
                            upgradeMJ8Licenses: this.upgradeMJ8Licenses,
                            upgradeProLicenses: this.upgradeProLicenses,
                        }
                    };
                }

                // Add the location to the array if it's not already present
                const locations = err._customContext[contextKey].errorThrownCallStack;
                if (!locations.includes(location)) {
                    locations.push(location);
                }

                return;
            },
        },
        computed: {
            ...mapState([
                'infoFromSoftware',
                'getProductsForPurchasePending',
                'getProductsForPurchaseData',

                'isDiscountCodeValidPending',

                'getLicensesCustomerData',
                'getLicensesCustomerPending',

                'processLicenseActivationPending',
                'processLicenseActivationData',

            ]),
            cart() { return this.$store.getters.cart },
            reinstatementLicenseItems() { return this.$store.getters.reinstatementLicenseItems },
            showReinstatementCostDialog() { return this.$store.getters.showReinstatementCostDialog },
            cartItemAdded() { return this.$store.getters.cartItemAdded },
            isLoggedIn() { return this.$store.getters.isLoggedIn },
            user() { return this.$store.getters.authUser },
            stepData() {
                switch (this.form.selectedOption) {
                    case 'newLicense':
                        return { icon: 'mdi-cart', caption: 'Purchase', multiLicenses: null }
                        break
                    case 'prepaid':
                        return { icon: 'mdi-lock-open-variant', caption: 'Activate', multiLicenses: null }
                        break
                    case 'renew':
                        return { icon: 'mdi-autorenew', caption: 'Renew', multiLicenses: this.renewLicenses }
                        break
                    case 'upgradeMJ8':
                        return { icon: 'mdi-arrow-up-bold', caption: 'Upgrade', multiLicenses: this.upgradeMJ8Licenses }
                        break
                    case 'upgradePro':
                        return { icon: 'mdi-arrow-up-bold', caption: 'Upgrade', multiLicenses: this.upgradeProLicenses }
                        break
                    case 'reactivate':
                        return { icon: 'mdi-lock-open-variant', caption: 'Activate', multiLicenses: null }
                        break
                    default:
                        return { icon: 'mdi-lock-open-variant', caption: 'Activate', multiLicenses: null }
                }
            },
            showContinue() {
                if (this.step == 2) return false
                if (this.step == 3 && this.form.selectedOption == 'renew') return (this.renewLicenses.length > 0)
                if (this.step == 3 && this.form.selectedOption == 'upgradeMJ8') return (this.upgradeMJ8Licenses.length > 0)
                if (this.step == 3 && this.form.selectedOption == 'upgradePro') return (this.upgradeProLicenses.length > 0)
                if (this.step == 3 && this.form.selectedOption == 'reactivate') return false
                return true
            },
            allProductLineProducts() {
                if (this.getProductsForPurchaseData && this.form.productLine)
                    return this.getProductsForPurchaseData?.filter(p => p.visible == true && p.productCategoryID == this.form.productLine?.name).sort((a, b) => a.sortOrder - b.sortOrder)
                return []
            },
            fullLicenseProducts() {
                if (this.getProductsForPurchaseData && this.form.productLine)
                    return this.getProductsForPurchaseData?.filter(p => p.isSubscription == false && p.isUpgrade == false && p.visible == true && p.productCategoryID == this.form.productLine?.name).sort((a, b) => a.sortOrder - b.sortOrder)
                return []
            },
            screenSize() {
                return this.$q.screen
            },
            subscriptionRenewalProducts() {
                if (this.getProductsForPurchaseData)
                    return this.getProductsForPurchaseData?.filter(p => p.isSubscription == true && p.visible == true)
                return []
            },
            upgradeProducts() {
                if (this.getProductsForPurchaseData)
                    return this.getProductsForPurchaseData?.filter(p => p.isUpgrade == true)
                return []
            },
            renewLicenses() {
                if (this.getLicensesCustomerData && this.form.productLine) {
                    var renewLicensesFromCustomerData = this.getLicensesCustomerData?.filter(l => l.canRenew == true && l.productCategory == this.form.productLine?.name && l.active).sort((a, b) => new Date(a.expiry) - new Date(b.expiry))

                    if (this.form.productLine?.name == 'PlumbingCAD') {
                        renewLicensesFromCustomerData = renewLicensesFromCustomerData.filter(l => !this.isExpired(l.expiry))
                    }

                    return renewLicensesFromCustomerData
                }
                return []
            },
            upgradeMJ8Licenses() {
                if (this.getLicensesCustomerData && this.form.productLine)
                    return this.getLicensesCustomerData?.filter(l => l.canUpgradeMJ8 == true && l.productCategory == this.form.productLine?.name && l.active).sort((a, b) => new Date(a.expiry) - new Date(b.expiry))
                return []
            },
            upgradeProLicenses() {
                if (this.getLicensesCustomerData && this.form.productLine)
                    return this.getLicensesCustomerData?.filter(l => l.canUpgradePro == true && l.productCategory == this.form.productLine?.name && l.active).sort((a, b) => new Date(a.expiry) - new Date(b.expiry))
                return []
            },

        },
        created() {
            try {
                if (this.$route.query) {
                    this.$store.commit('update_inforFromSoftware', this.$route.query)
                    if (this.infoFromSoftware?.installId) this.form.installId = this.infoFromSoftware?.installId
                    if (this.infoFromSoftware?.newLicense) this.form.selectedOption = 'newLicense'
                    if (this.infoFromSoftware?.renew) this.form.selectedOption = 'renew'
                    if (this.infoFromSoftware?.prepaid) this.form.selectedOption = 'prepaid'
                    if (this.infoFromSoftware?.upgradeLicense) this.form.selectedOption = 'upgradeMJ8'
                    if (this.infoFromSoftware?.move) this.form.selectedOption = 'reactivate'
                }

                if (!this.productLine || (this.productLine.toLowerCase() !== 'loopcad' && this.productLine.toLowerCase() !== 'heatcad' && this.productLine.toLowerCase() !== 'plumbingcad')) {
                    this.$router.push('/activation/loopcad').catch(err => { })
                }

                this.updateProductLine()
                this.getProductsForPurchase()
                if (!this.form.productLine)
                    this.form.productLine = this.productLines[0]
                this.getCustomerLicenses()
            } catch (err) {
                this.addCustomErrorContext(err, "Hook: created")
                throw err;
            }
        },
        watch: {
            cartItemAdded(newVal) {
                if (newVal) {
                    if (!this.showReinstatementCostDialog) {
                        this.$router.push('/store/cart').catch(err => { })
                    }
                }
            },
            productLine(newVal, oldVal) {
                try {
                    if (newVal && newVal !== oldVal) {
                        this.updateProductLine()
                        this.form.selectedOption = 'reactivate'
                        this.step = 1
                    }
                } catch (err) {
                    this.addCustomErrorContext(err, "Watcher: productLine")
                    throw err;
                }
            },
            isLoggedIn(newVal, oldVal) {
                try {
                    if (newVal) {
                        this.showLogin = false;
                        this.getCustomerLicenses()
                        this.step = 3
                    }
                    else {
                        this.step = 1
                    }
                } catch (err) {
                    this.addCustomErrorContext(err, "Watcher: isLoggedIn")
                    throw err;
                }
            },
            step(newVal) {
                try {
                    if (newVal == 2) {
                        if (this.form.selectedOption !== 'newLicense' && this.form.selectedOption !== 'prepaid')
                            this.showLogin = true
                    }
                } catch (err) {
                    this.addCustomErrorContext(err, "Watcher: step")
                    throw err;
                }
            },
            '$route.query.option'(newVal, oldVal) {
                if (newVal && newVal !== oldVal) {
                    this.form.selectedOption = newVal
                    this.step = 1
                }
            },
            'form.selectedOption'(newVal) {
                try {
                    this.updateRoute(newVal)
                    switch (newVal) {
                        case 'newLicense':
                            this.selectDefaultProduct()
                            break
                        case 'prepaid':
                            this.form.product = null
                            break
                        case 'renew':
                            this.form.product = this.subscriptionRenewalProducts?.find(p => p.productCategoryID == this.form.productLine?.name)
                            break
                        case 'upgradeMJ8':
                            this.form.product = this.upgradeProducts?.find(p => p.productCategoryID == this.form.productLine?.name && p.edition == 'Professional' && p.id.includes('MJ8'))
                            break
                        case 'upgradePro':
                            this.form.product = this.upgradeProducts?.find(p => p.productCategoryID == this.form.productLine?.name && p.edition == 'Standard' && p.id.includes('PF'))
                            break
                        case 'reactivate':
                            this.form.product = null
                            break
                        default:
                            this.form.product = this.fullLicenseProducts[this.fullLicenseProducts.length - 1]
                    }
                } catch (err) {
                    this.addCustomErrorContext(err, "Watcher: form.selectedOption")
                    throw err;
                }
            },
        },
        errorCaptured(err, vm, info) {
            this.addCustomErrorContext(err, "Hook: errorCaptured (Comes from Child Component)");
            return;
        },
    }
</script>
<style lang="scss">
    @import "../styles/quasar.variables.scss";

    @media (max-width: $breakpoint-xs-max) {
        .q-stepper__step-inner, .q-stepper__tab {
            padding: 0 4px !important;
        }

        .q-stepper__nav {
            padding: 24px 0 0 0 !important;
        }
    }

    .img-activation {
        max-width: 100%;
        max-height: 55px;
        width: unset;
    }
</style>
